import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { uploadPhoto, getPhotoByDate, API_BASE_URL } from '../services/api';
import {
  formatDateToYYYYMMDD,
  formatDateDisplay,
  getRatingColor,
  getRatingLabel,
  isPhotoDraft,
  isValidYYYYMMDD,
} from '../utils/dateUtils';

const parseDateParam = (dateString) => {
  const [y, m, d] = dateString.split('-').map(Number);
  const date = new Date(y, m - 1, d, 12, 0, 0);
  return date;
};

const UploadPage = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();

  const getToday = () => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    return date;
  };

  const getInitialDate = () => {
    const dateParam = searchParams.get('date');
    if (dateParam && isValidYYYYMMDD(dateParam)) {
      return parseDateParam(dateParam);
    }
    return getToday();
  };

  const [currentDate, setCurrentDate] = useState(getInitialDate);
  const currentDateString = formatDateToYYYYMMDD(currentDate);

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [existingPhoto, setExistingPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [description, setDescription] = useState('');
  const [rating, setRating] = useState(null);

  const handlePreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
    // Reset form state when changing date
    setSelectedFile(null);
    setPreview(null);
    setMessage({ type: '', text: '' });
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
    // Reset form state when changing date
    setSelectedFile(null);
    setPreview(null);
    setMessage({ type: '', text: '' });
  };

  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam && isValidYYYYMMDD(dateParam)) {
      setCurrentDate(parseDateParam(dateParam));
      setSelectedFile(null);
      setPreview(null);
      setMessage({ type: '', text: '' });
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchPhoto = async () => {
      try {
        setFetching(true);
        const photo = await getPhotoByDate(currentDateString);
        setExistingPhoto(photo);
        setDescription(photo.description || '');
        setRating(photo.rating ?? null);
      } catch (error) {
        // Photo doesn't exist for this date, which is fine
        if (error.response?.status !== 404) {
          console.error('Error fetching photo:', error);
        }
        setExistingPhoto(null);
        setDescription('');
        setRating(null);
      } finally {
        setFetching(false);
      }
    };

    fetchPhoto();
  }, [currentDateString]);

  const handleRatingChange = (event, newRating) => {
    if (newRating !== null) {
      setRating(newRating);
    }
  };


  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo?.file_path) return null;
    if (photo.file_path.startsWith('http')) {
      return photo.file_path;
    }
    return `${API_BASE_URL}${photo.file_path}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const photo = await uploadPhoto(currentDateString, selectedFile, description, rating);
      const savedAsDraft = isPhotoDraft(photo);
      setMessage({
        type: 'success',
        text: savedAsDraft
          ? 'Draft saved. Add photo, description, and rating to complete this day.'
          : 'Day saved successfully!',
      });
      setSelectedFile(null);
      setPreview(null);
      setExistingPhoto(photo);
      e.target.reset();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        pt: { xs: 10, sm: 12 }, 
        mb: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <Paper elevation={0} sx={{ pb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Date Navigation Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: { xs: 2, sm: 3 } }}>
          <IconButton onClick={handlePreviousDay} size={isSmallScreen ? 'medium' : 'large'}>
            <ArrowBackIosIcon />
          </IconButton>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              mx: { xs: 1, sm: 2 }, 
              textAlign: 'center', 
              minWidth: { xs: 200, sm: 280 },
              fontSize: { xs: '1.5rem', sm: '2rem' },
            }}
          >
            {formatDateDisplay(currentDateString)}
          </Typography>
          <IconButton onClick={handleNextDay} size={isSmallScreen ? 'medium' : 'large'}>
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>

        {existingPhoto && isPhotoDraft(existingPhoto) && !message.text && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This day is saved as a draft. Add a photo, description, and rating to complete it.
          </Alert>
        )}

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        {fetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              {(preview || getPhotoUrl(existingPhoto)) ? (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  bgcolor: '#fafafa',
                }}
              >
                <Box
                  component="img"
                  src={preview || getPhotoUrl(existingPhoto) || ''}
                  alt={preview ? "Preview" : "Today's photo"}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
                <Box
                  component="label"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    cursor: 'pointer',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 1,
                    p: 1,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 1)',
                    },
                  }}
                >
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <AddIcon sx={{ color: 'gray', fontSize: 32 }} />
                </Box>
              </Box>
            ) : (
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  aspectRatio: '1',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: '#fafafa',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderColor: '#bdbdbd',
                  },
                }}
              >
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <AddIcon sx={{ color: 'gray', fontSize: 80 }} />
              </Box>
            )}
          </Box>

            {/* Day Rating */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                How was your day?
              </Typography>
              <ToggleButtonGroup
                value={rating}
                exclusive
                onChange={handleRatingChange}
                fullWidth
                sx={{ mb: 1 }}
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    sx={{
                      flex: 1,
                      bgcolor: rating === value ? getRatingColor(value) : 'transparent',
                      color: rating === value ? '#fff' : 'inherit',
                      borderColor: getRatingColor(value),
                      '&.Mui-selected': {
                        bgcolor: getRatingColor(value),
                        color: '#fff',
                        '&:hover': {
                          bgcolor: getRatingColor(value),
                        },
                      },
                      '&:hover': {
                        bgcolor: `${getRatingColor(value)}33`,
                      },
                    }}
                  >
                    {value}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {rating != null ? getRatingLabel(rating) : 'Optional — tap a number to rate your day'}
              </Typography>
            </Box>

            {/* Description */}
            <TextField
              label="Day Description"
              multiline
              rows={3}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="How was your day? What happened?"
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Saving...' : 'Save Day'}
            </Button>
          </form>
        )}
        </Paper>
      </Container>
  );
};

export default UploadPage;

